import { AxiosInstance } from 'axios';
import { IMotionSchema } from '@/shared/api-client/models/motion/motion-schema';
import { QueryStringBuilder } from '@/shared/api-client/helpers';
import { INewMotion } from '@/shared/api-client/models/motion/new-motion';
import { IPagedList, IPagination } from '@/shared/api-client/models';
import { IMotion } from '@/shared/api-client/models/motion/motion';
import { IEditableMotion } from '@/shared/api-client/models/motion/editable-motion';
import { ArgumentValidator } from '@/shared/helpers';
import { IMotionListEntry } from '@/shared/api-client/models/motion/motion-list-entry';
import { INewMotionReply } from '../models/motion/new-motion-reply';
import { IOrganizationalPathLevel } from '../models/organizational-path-level';
import { IMyMotionsListEntry } from '../models/motion/my-motions-list-entry';

export class MotionClient {

    constructor(private axiosClient: AxiosInstance) { }

    public get apiUrl() {
        return `/motion`;
    }

    public async schema(activityId: string, cultureAlias: string): Promise<IMotionSchema> {

        try {

            const url = new QueryStringBuilder()
                .withCultureAliasIfNotEmpty(cultureAlias)
                .build(`${this.apiUrl}/${activityId}/schema`);

            const response = await this.axiosClient.get(url);

            return response.data;

        } catch (error: any) {

            throw error.response.data;
        }
    }

    public async create(activityId: string, newMotion: INewMotion, motioningAccessTicketId?: string) {

        ArgumentValidator.notNullOrEmpty(activityId, `activityId`);
        ArgumentValidator.notNullOrEmpty(newMotion, `newMotion`);

        const url = new QueryStringBuilder()
            .withQueryIfNotEmpty('motioningAccessTicketId', motioningAccessTicketId)
            .build(`${this.apiUrl}/${activityId}`);

        try {
            await this.axiosClient.post(
                url,
                newMotion,
                {
                    headers: {
                        'Content-Type': 'application/json;charset=UTF-8'
                    }
                }
            );

        } catch (error: any) {

            throw error.response.data;
        }
    }

    public async update(activityId: string, motionId: string, editableMotion: IEditableMotion): Promise<IMotion> {

        ArgumentValidator.notNullOrEmpty(activityId, `activityId`);
        ArgumentValidator.notNullOrEmpty(motionId, `motionId`);
        ArgumentValidator.notNullOrEmpty(editableMotion, `editableMotion`);

        try {
            const response = await this.axiosClient.put(
                `${this.apiUrl}/${activityId}/${motionId}`,
                editableMotion,
                {
                    headers: {
                        'Content-Type': 'application/json;charset=UTF-8'
                    }
                }
            );

            return response.data;

        } catch (error: any) {

            throw error.response.data;
        }
    }

    public async delete(activityId: string, motionId: string): Promise<void> {

        ArgumentValidator.notNullOrEmpty(activityId, `activityId`);
        ArgumentValidator.notNullOrEmpty(motionId, `motionId`);

        const response = await this.axiosClient.delete(`${this.apiUrl}/${activityId}/${motionId}`);

        return response.data;
    }

    public async list(activityId: string, paging?: IPagination): Promise<IPagedList<IMotionListEntry>> {

        try {
            const url = new QueryStringBuilder()
                .withPaginationIfNotEmpty(paging)
                .build(`${this.apiUrl}/${activityId}/list`);

            const response = await this.axiosClient.get(
                url,
                {
                    headers: {
                        'Content-Type': 'application/json;charset=UTF-8'
                    }
                }
            );

            return response.data;

        } catch (error: any) {

            throw error.response.data;
        }
    }

    public async listMyMotions(pagination?: IPagination): Promise<IPagedList<IMyMotionsListEntry>> {

        try {

            const url = new QueryStringBuilder()
                .withPaginationIfNotEmpty(pagination)
                .build(`${this.apiUrl}`);

            const response = await this.axiosClient.get(url);

            return response.data;

        } catch (error: any) {

            throw error.response.data;
        }
    }

    public async resubmit(activityId: string, motionId: string): Promise<void> {

        try {

            const url = new QueryStringBuilder()
                .build(`${this.apiUrl}/${activityId}/${motionId}/resubmit`);

            const response = await this.axiosClient.post(url);

            return response.data;

        } catch (error: any) {

            throw error.response.data;
        }
    }

    public async get(id: string): Promise<IMotion> {
        try {
            const url = new QueryStringBuilder()
                .build(`${this.apiUrl}/${id}`);

            const response = await this.axiosClient.get(
                url,
                {
                    headers: {
                        'Content-Type': 'application/json;charset=UTF-8'
                    }
                }
            );

            return response.data;

        } catch (error: any) {

            throw error.response.data;
        }
    }

    public async assignCurrentDecisionLevel(id: string, userId: string): Promise<IMotion> {

        ArgumentValidator.notNullOrEmpty(id, `id`);
        ArgumentValidator.notNullOrEmpty(userId, `userId`);

        try {

            const response = await this.axiosClient.post(
                `${this.apiUrl}/${id}/assignCurrentDecisionLevel`,
                {
                    userId: userId
                },
                {
                    headers: {
                        'Content-Type': 'application/json;charset=UTF-8'
                    }
                }
            );

            return response.data;

        } catch (error: any) {

            throw error.response.data;
        }
    }

    public async assignDecisionLevel(id: string, userId: string, level: number): Promise<IMotion> {

        ArgumentValidator.notNullOrEmpty(id, `id`);
        ArgumentValidator.notNullOrEmpty(userId, `userId`);
        ArgumentValidator.notNullOrEmpty(level, `level`);

        try {

            const response = await this.axiosClient.post(
                `${this.apiUrl}/${id}/assignDecisionLevel/${level}`,
                {
                    userId: userId
                },
                {
                    headers: {
                        'Content-Type': 'application/json;charset=UTF-8'
                    }
                }
            );

            return response.data;

        } catch (error: any) {

            throw error.response.data;
        }
    }

    public async reply(id: string, newReply: INewMotionReply): Promise<IMotion> {

        ArgumentValidator.notNullOrEmpty(id, `id`);
        ArgumentValidator.notNullOrEmpty(newReply, `newReply`);

        try {

            const response = await this.axiosClient.post(
                `${this.apiUrl}/${id}/reply`,
                newReply,
                {
                    headers: {
                        'Content-Type': 'application/json;charset=UTF-8'
                    }
                }
            );

            return response.data;

        } catch (error: any) {

            throw error.response.data;
        }
    }

    public async finishCurrentDecisionLevel(id: string): Promise<IMotion> {

        ArgumentValidator.notNullOrEmpty(id, `id`);

        try {

            const response = await this.axiosClient.post(`${this.apiUrl}/${id}/finishCurrentDecisionLevel`);

            return response.data;

        } catch (error: any) {

            throw error.response.data;
        }
    }

    public async rejectCurrentDecisionLevel(id: string): Promise<IMotion> {

        ArgumentValidator.notNullOrEmpty(id, `id`);

        try {

            const response = await this.axiosClient.post(`${this.apiUrl}/${id}/rejectCurrentDecisionLevel`);

            return response.data;

        } catch (error: any) {

            throw error.response.data;
        }
    }

    public async listDecisionLevels(id: string): Promise<IOrganizationalPathLevel[]> {

        try {
            const url = new QueryStringBuilder()
                .build(`${this.apiUrl}/${id}/decisionLevels`);

            const response = await this.axiosClient.get(
                url,
                {
                    headers: {
                        'Content-Type': 'application/json;charset=UTF-8'
                    }
                }
            );

            return response.data;

        } catch (error: any) {

            throw error.response.data;
        }
    }

}
